import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchUsers, deleteUser, bulkDeleteUsers, bulkUpdateUserStatus } from "./userSlice";
import Swal from 'sweetalert2';
import ConfirmationModal from "../../components/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import log from "../../logger";
import ReactTable from "../../components/ReactTable";
import { paths } from "../../routes/helpers";
import { getImageUrl } from "../../utils/urlUtils";

const UserList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, total, pages } = useSelector(
    (state) => state.user
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    dispatch(fetchUsers({ page: currentPage, limit: currentLimit, filters: { search: searchTerm } }));
  }, [dispatch, currentPage, currentLimit, searchTerm]);

  const handleDelete = (userId) => {
    setShowDeleteModal(true);
    setSelectedUser(userId);
  };

  const confirmDelete = () => {
    if (selectedUser) {
      dispatch(deleteUser(selectedUser));
      toast.success("User deleted successfully!");
      log.info("User deleted:", selectedUser);
      setShowDeleteModal(false);
      setSelectedUser(null);
    }
  };

  const handleEdit = (userId) => {
    navigate(paths.private.user.edit(userId));
  };

  const handleCreateUser = () => {
    navigate(paths.private.user.create());
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setCurrentLimit(newLimit);
    setCurrentPage(1);
    dispatch(fetchUsers({ page: 1, limit: newLimit, filters: { search: searchTerm } }));
  };

  const handleSearch = (value) => {
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page when searching
    }, 500); // 500ms delay

    setSearchTimeout(timeout);
  };

  const handleBulkDelete = async (ids, clearSelection) => {
    const result = await Swal.fire({
      title: `Delete ${ids.length} user${ids.length > 1 ? 's' : ''}?`,
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    dispatch(bulkDeleteUsers(ids)).then(() => {
      Swal.fire({ title: 'Deleted!', text: `${ids.length} user${ids.length > 1 ? 's' : ''} deleted.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const handleBulkStatusChange = (status) => async (ids, clearSelection) => {
    const activating = status === 'ACTIVE';
    const result = await Swal.fire({
      title: `${activating ? 'Activate' : 'Deactivate'} ${ids.length} user${ids.length > 1 ? 's' : ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${activating ? 'activate' : 'deactivate'}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    dispatch(bulkUpdateUserStatus({ ids, status })).then(() => {
      Swal.fire({ title: 'Updated!', text: `${ids.length} user${ids.length > 1 ? 's' : ''} ${activating ? 'activated' : 'deactivated'}.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const formatTableData = (users) => {
    return users.map((user) => ({
      _id: user._id,
      profilePic: (
        <div className="relative">
          {user.profilePic ? (
            <img 
              src={getImageUrl(user.profilePic)} 
              alt={user.username || 'User'} 
              className="w-8 h-8 rounded-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-xs text-white font-semibold ${
              user.profilePic ? 'hidden' : ''
            }`}
            style={{ display: user.profilePic ? 'none' : 'flex' }}
          >
            {(user.username || user.email || '?').charAt(0).toUpperCase()}
          </div>
        </div>
      ),
      username: user.username || '-',
      email: user.email || '-',
      role: user.role || '-',
      status: user.status || '-',
      emailVerified: user.emailVerified ? 'Yes' : 'No',
      twoFactorEnabled: user.twoFactorEnabled ? 'Yes' : 'No',
      lastLogin: user.lastLogin ? new Date(user.lastLogin).toLocaleString() : '-',
      createdAt: user.createdAt ? new Date(user.createdAt).toLocaleString() : '-'
    }));
  };

  const headers = [
    { label: 'Profile', key: 'profilePic' },
    { label: 'Username', key: 'username' },
    { label: 'Email', key: 'email' },
    { label: 'Role', key: 'role' },
    { label: 'Status', key: 'status' },
    { label: 'Email Verified', key: 'emailVerified' },
    { label: '2FA Enabled', key: 'twoFactorEnabled' },
    { label: 'Last Login', key: 'lastLogin' },
    { label: 'Created At', key: 'createdAt' }
  ];

  const actions = [
    { label: 'Edit', onClick: handleEdit, color: 'blue' },
    { label: 'Delete', onClick: handleDelete, color: 'red' },
  ];

  if (error) return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleCreateUser}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          Create New User
        </button>
      </div>
      <div className="text-red-500">Error: {error}</div>
    </div>
  );

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Users</h1>
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleCreateUser}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          Create New User
        </button>
        <div className="text-sm text-gray-600">Total: {total} users</div>
      </div>

      <ReactTable
        headers={headers}
        data={formatTableData(items)}
        actions={actions}
        onSearch={handleSearch}
        searchValue={searchTerm}
        loading={loading}
        pagination={items.length > 0 ? { page: currentPage, limit: currentLimit, total, pages } : null}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        enableRowSelection={true}
        bulkActions={[
          { label: 'Activate Selected', color: 'green', onClick: handleBulkStatusChange('ACTIVE') },
          { label: 'Deactivate Selected', color: 'gray', onClick: handleBulkStatusChange('DEACTIVATED') },
          { label: 'Delete Selected', color: 'red', onClick: handleBulkDelete },
        ]}
      />

      {items.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No users found</p>
          <p className="text-gray-400 text-sm mt-2">Click the Create New button to add one</p>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this user?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default UserList;
